/*
 * Valenor — Variant picker.
 * Simple, defensive client-side variant matching:
 *   - Read product.variants JSON from <script data-vln-variants>
 *   - On any interaction in .vln-product-hero__variants, walk each
 *     fieldset (data-option-position) and collect the clicked label's
 *     value from .vln-variant-opt--selected (or the native :checked input).
 *   - Find the variant whose option[position] match the selected values.
 *   - Push id + price + gallery + sticky.
 *
 * Uses pointerup + change + click to catch every interaction path and
 * logs every decision to the console with a "[vln-variant]" prefix so
 * issues are diagnosable without re-deploying.
 */
(() => {
  if (typeof window === 'undefined' || window.__vlnVariantInit) return;
  window.__vlnVariantInit = true;

  const LOG = (...a) => console.log('[vln-variant]', ...a);
  const WARN = (...a) => console.warn('[vln-variant]', ...a);

  const _docLang = (typeof document !== 'undefined' ? (document.documentElement.lang || '') : '').toLowerCase();
  const _isEN = _docLang.startsWith('en');
  const moneyFormatter =
    typeof Intl !== 'undefined'
      ? new Intl.NumberFormat(_isEN ? 'en-GB' : 'fr-FR', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
        })
      : null;

  const formatMoney = (cents) => {
    if (typeof cents !== 'number') return '';
    if (moneyFormatter) return moneyFormatter.format(cents / 100);
    if (_isEN) return '€' + (cents / 100).toFixed(2);
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  };

  const init = () => {
    const hero = document.querySelector('.vln-product-hero');
    if (!hero) return;

    const variantsScript = hero.querySelector('[data-vln-variants]');
    if (!variantsScript) {
      WARN('no variants JSON found');
      return;
    }
    let variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      WARN('variants JSON parse failed', e);
      return;
    }
    LOG('boot', { variantsCount: variants.length });

    const form = hero.querySelector('.vln-atc form');
    const idInput = form ? form.querySelector('input[name="id"]') : null;
    const atcBtn = form ? form.querySelector('button[type="submit"]') : null;
    const atcLabel = atcBtn ? atcBtn.querySelector('.vln-btn__label') : null;
    const atcPrice = atcBtn ? atcBtn.querySelector('.vln-btn__price') : null;
    const priceEl = hero.querySelector('.vln-price__current');
    const compareEl = hero.querySelector('.vln-price__compare');
    const badgeEl = hero.querySelector('.vln-price__badge');
    const noteEl = hero.querySelector('.vln-price__note');
    const stickyBar = document.querySelector('.vln-sticky-atc');
    const stickyCurrent = stickyBar ? stickyBar.querySelector('.vln-sticky-atc__current') : null;
    const stickyCompare = stickyBar ? stickyBar.querySelector('.vln-sticky-atc__compare') : null;
    const stickyImg = stickyBar ? stickyBar.querySelector('.vln-sticky-atc__img') : null;

    const collectSelected = () => {
      const groups = Array.from(hero.querySelectorAll('.vln-variant-group'));
      // Sort by data-option-position so selected[0] = option1, selected[1] = option2, …
      groups.sort(
        (a, b) =>
          parseInt(a.dataset.optionPosition || 0, 10) -
          parseInt(b.dataset.optionPosition || 0, 10)
      );
      const selected = [];
      groups.forEach((g) => {
        const input = g.querySelector('input[type="radio"]:checked');
        selected.push(input ? input.value : null);
      });
      return selected;
    };

    const pickVariant = () => {
      const selected = collectSelected();
      const match = variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3]
          .filter((o) => o != null);
        if (opts.length !== selected.filter(Boolean).length) return false;
        return opts.every((o, i) => o === selected[i]);
      });
      if (!match) WARN('no variant match for', selected);
      return match;
    };

    const switchLifestyleAndMaterialsToVariant = (variantId) => {
      // Per-variant blocks outside the hero: lifestyle gallery groups +
      // materials macro images. Each carries `data-variant-ids="{id}"`.
      const extras = document.querySelectorAll(
        '.vln-lifestyle-gallery__variant-group[data-variant-ids], ' +
          '.vln-materials-grid__image[data-variant-ids]'
      );
      extras.forEach((el) => {
        const owners = (el.dataset.variantIds || '').split(',');
        if (owners.includes(variantId)) {
          el.removeAttribute('data-hidden');
        } else {
          el.setAttribute('data-hidden', '');
        }
      });
    };

    const switchGalleryToVariant = (variant) => {
      const variantId = String(variant.id);
      const slides = hero.querySelectorAll('.vln-product-hero__media-slide');
      const thumbs = hero.querySelectorAll('.vln-thumb');

      // Lifestyle + materials sections (outside the hero).
      switchLifestyleAndMaterialsToVariant(variantId);

      // Does ANY slide own this variant? If not, don't hide anything
      // (variant has no image; fall back to showing all media).
      const anyOwner = Array.from(slides).some((s) =>
        (s.dataset.variantIds || '').split(',').includes(variantId)
      );

      const matches = (el) => {
        if (!anyOwner) return true;
        const owners = el.dataset.variantIds;
        if (!owners) return true; // generic
        return owners.split(',').includes(variantId);
      };

      slides.forEach((s) => {
        if (matches(s)) s.removeAttribute('data-hidden');
        else s.setAttribute('data-hidden', '');
      });
      thumbs.forEach((t) => {
        if (matches(t)) t.removeAttribute('data-hidden');
        else t.setAttribute('data-hidden', '');
      });

      if (!variant.featured_media || !variant.featured_media.id) return;
      const mediaId = String(variant.featured_media.id);
      const slide = hero.querySelector(
        `.vln-product-hero__media-slide[data-media-id="${mediaId}"]`
      );
      if (!slide) return;
      slides.forEach((s) => s.removeAttribute('data-active'));
      slide.setAttribute('data-active', '');

      if (window.matchMedia('(max-width: 900px)').matches) {
        slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }

      const thumb = hero.querySelector(
        `.vln-thumb[data-media-id="${mediaId}"]`
      );
      if (thumb) {
        hero
          .querySelectorAll('.vln-thumb')
          .forEach((t) => t.classList.remove('vln-thumb--active'));
        thumb.classList.add('vln-thumb--active');
        const thumbRow = thumb.closest('.vln-product-hero__thumbs');
        if (thumbRow && thumbRow.scrollWidth > thumbRow.clientWidth + 1) {
          const target =
            thumb.offsetLeft - thumbRow.clientWidth / 2 + thumb.clientWidth / 2;
          thumbRow.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
        }
      }
    };

    const update = () => {
      const variant = pickVariant();
      if (!variant) return;
      LOG('update', variant.id, variant.option1, variant.option2, variant.option3);

      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (atcPrice) atcPrice.textContent = formatMoney(variant.price);

      const hasCompare =
        variant.compare_at_price && variant.compare_at_price > variant.price;
      if (compareEl) {
        compareEl.style.display = hasCompare ? '' : 'none';
        if (hasCompare) compareEl.textContent = formatMoney(variant.compare_at_price);
      }
      if (badgeEl) {
        if (hasCompare) {
          const pct = Math.round(
            ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
          );
          badgeEl.textContent = `−${pct}%`;
          badgeEl.style.display = '';
        } else {
          badgeEl.style.display = 'none';
        }
      }
      const isEN = (document.documentElement.lang || '').toLowerCase().startsWith('en');
      if (noteEl) {
        if (hasCompare) {
          const saved = variant.compare_at_price - variant.price;
          const offerUntil = noteEl.dataset.vlnOfferUntil || '';
          if (isEN) {
            noteEl.textContent = offerUntil
              ? `Save ${formatMoney(saved)} · offer until ${offerUntil}`
              : `Save ${formatMoney(saved)}`;
          } else {
            noteEl.textContent = offerUntil
              ? `Économie de ${formatMoney(saved)} · offre jusqu'au ${offerUntil}`
              : `Économie de ${formatMoney(saved)}`;
          }
          noteEl.style.display = '';
        } else {
          noteEl.style.display = 'none';
        }
      }
      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        if (!variant.available && atcLabel) {
          atcLabel.textContent = isEN ? 'Out of stock' : 'Rupture de stock';
        }
      }
      if (stickyCurrent) stickyCurrent.textContent = formatMoney(variant.price);
      if (stickyImg && variant.featured_image && variant.featured_image.src) {
        // Shopify image.src already starts with `//cdn.shopify.com…`; let the
        // browser normalise the scheme. Request a small 96px render.
        let src = variant.featured_image.src;
        if (src.startsWith('//')) src = 'https:' + src;
        // Insert a `_96x` before the file extension for a smaller file.
        src = src.replace(/\.(jpg|jpeg|png|webp|gif)(\?.*)?$/i, '_96x.$1$2');
        stickyImg.src = src;
      }
      if (stickyCompare) {
        if (hasCompare) {
          stickyCompare.textContent = formatMoney(variant.compare_at_price);
          stickyCompare.hidden = false;
          stickyCompare.removeAttribute('hidden');
        } else {
          stickyCompare.hidden = true;
          stickyCompare.setAttribute('hidden', '');
        }
      }

      try {
        switchGalleryToVariant(variant);
      } catch (e) {
        WARN('gallery switch failed', e);
      }

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      } catch (_) {}
    };

    const applySelection = (label) => {
      if (!label) return;
      const input = label.querySelector('input[type="radio"]');
      if (!input) return;
      const group = label.closest('.vln-variant-options');
      if (group) {
        group.querySelectorAll('.vln-variant-opt').forEach((l) =>
          l.classList.remove('vln-variant-opt--selected')
        );
      }
      label.classList.add('vln-variant-opt--selected');
      input.checked = true;
      LOG('selected', label.dataset.optionValue);
      update();
    };

    // Single authoritative click handler — covers every browser path.
    hero.addEventListener('click', (e) => {
      const label = e.target.closest('.vln-variant-opt');
      if (!label) return;
      applySelection(label);
    });

    // Keyboard / assistive input change.
    hero.addEventListener('change', (e) => {
      const input = e.target;
      if (!input.matches || !input.matches('.vln-variant-group input[type="radio"]')) return;
      applySelection(input.closest('.vln-variant-opt'));
    });

    // Initial sync: filter the gallery to the pre-selected variant on mount
    // so shoppers don't see other variants' images before touching the picker.
    try {
      update();
    } catch (e) {
      WARN('initial update failed', e);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
